import 'braid-design-system/reset';
import {
  useExperimentValue,
  useFeatureFlag,
} from '@seek/candidate-feature-flag';

import {
  APPLICANT_INSIGHTS_NUMBER_OF_APPLICANTS_FEATURE_FLAG,
  type ApplicantInsightNumberOfApplicantsExperiment,
  BEHAVIOURAL_CUES_FILTERS_FEATURE_FLAG,
  BehaviouralCuesFilters,
  type BehaviouralCuesFiltersExperiment,
  DynamicPillsV2,
  JobCardRedesign,
  IN_FLOW_NUDGING_FEATURE_FLAG,
  type InFlowNudgingExperiment,
  LOCATION_PROMPT_FEATURE_FLAG,
  type LocationPromptExperiment,
  REFINE_BAR_V2_FEATURE_FLAG,
  RefineBarV2,
  type RefineBarV2Experiment,
  SAVED_AND_RECENT_SEARCHES_FEATURE_FLAG,
  type SavedAndRecentSearchesExperiment,
  SEARCH_QUERY_CORRECTION_FEATURE_FLAG,
  type SearchQueryCorrectionExperiment,
  SERP_JOB_CARD_REDESIGN_FEATURE_FLAG,
  type SERPJobCardRedesignExperiment,
  StickySearchBar,
  CAREER_FEED_COMPANY_EVP_FEATURE_FLAG,
  type CareerFeedCompanyEvpExperiment,
  type CareerFeedJobAiSummariesExperiment,
  CAREER_FEED_JOB_AI_SUMMARIES_FEATURE_FLAG,
  InFlowNudgingWithoutCounts,
  InFlowNudgingWithCounts,
  JDV_JOB_MATCH_ANALYSIS_FEATURE_FLAG,
  type JobMatchAnalysisExperiment,
} from 'src/config/experiments';
import {
  HOME_FEEDBACK_LOOPS_HIDE_JOB_FEATURE_FLAG,
  type HomeFeedbackLoopsHideJobFeatureFlag,
  JDV_JOB_MODE_FEATURE_FLAG,
  type JdvJobModeFeatureFlag,
  LD_HOMEPAGE_GENAI_CHAT_FEATURE_FLAG,
  type LDHomepageGenAIChatFeatureFlag,
  SOURCR_ON_SRP_JOB_CARD_FEATURE_FLAG,
  type SourcrOnSRPJobCardFeatureFlag,
  STRUCTURED_FEEDBACK_LOOPS_PHASE_1_FEATURE_FLAG,
  type StructuredFeedbackLoopsPhase1FeatureFlag,
} from 'src/config/featureFlags';

import { useSelector } from '../react';

import { selectFeatureFlag } from './selectors';

export const useIsCareerFeedCompanyEvpEnabled = () => {
  const value = useExperimentValue<CareerFeedCompanyEvpExperiment>(
    CAREER_FEED_COMPANY_EVP_FEATURE_FLAG,
  );

  return value === 'show';
};

export const useIsHomepageGenAIChatKillSwitchEnabled = () =>
  !useLdHomepageGenAIChatFeatureFlag();

const useGetIsHomepageGenAIChatEnabled = () => {
  const isKillSwitchEnabled = useIsHomepageGenAIChatKillSwitchEnabled();

  return (featureFlags: boolean) => !isKillSwitchEnabled && featureFlags;
};

export const useIsHomepageGenAIChatEnabled = () => {
  const isHomepageGenAIChatEnabled = useSelector(
    selectFeatureFlag('homepageGenAIChatTest'),
  );
  const getIsHomepageGenAIChatEnabled = useGetIsHomepageGenAIChatEnabled();

  return getIsHomepageGenAIChatEnabled(isHomepageGenAIChatEnabled);
};

export const useIsHomepageGenAIChatAltModelEnabled = () =>
  useSelector(selectFeatureFlag('homepageGenAIChatAltModel'));

export const useFeatureFlagRefineBarV2Experiment = () => {
  const value = useExperimentValue<RefineBarV2Experiment>(
    REFINE_BAR_V2_FEATURE_FLAG,
  );

  return {
    dynamicPillsV2: value === DynamicPillsV2,
    stickySearchBar: value === StickySearchBar,
    refineBarV2: value === RefineBarV2,
  };
};

export const useSavedAndRecentSearchesFeatureFlag = () => {
  const value = useExperimentValue<SavedAndRecentSearchesExperiment>(
    SAVED_AND_RECENT_SEARCHES_FEATURE_FLAG,
  );

  return value === 'saved-and-recent-searches';
};

export const useLdHomepageGenAIChatFeatureFlag = () => {
  const value = useFeatureFlag<LDHomepageGenAIChatFeatureFlag>(
    LD_HOMEPAGE_GENAI_CHAT_FEATURE_FLAG,
  );

  return value;
};

export const useSERPJobCardRedesignFeatureFlag = () => {
  const value = useExperimentValue<SERPJobCardRedesignExperiment>(
    SERP_JOB_CARD_REDESIGN_FEATURE_FLAG,
  );

  return value === JobCardRedesign;
};

export const useBehaviouralCuesFiltersFeatureFlag = () => {
  const value = useExperimentValue<BehaviouralCuesFiltersExperiment>(
    BEHAVIOURAL_CUES_FILTERS_FEATURE_FLAG,
  );

  return value === BehaviouralCuesFilters;
};

export const useSearchQueryCorrectionFeatureFlag = () => {
  const value = useExperimentValue<SearchQueryCorrectionExperiment>(
    SEARCH_QUERY_CORRECTION_FEATURE_FLAG,
  );
  return value === 'search-query-correction';
};

export const useSourcrOnSRPJobCardFeatureFlag = () =>
  useFeatureFlag<SourcrOnSRPJobCardFeatureFlag>(
    SOURCR_ON_SRP_JOB_CARD_FEATURE_FLAG,
  );

export const useHomeFeedbackLoopsHideJobFeatureFlag = () =>
  useFeatureFlag<HomeFeedbackLoopsHideJobFeatureFlag>(
    HOME_FEEDBACK_LOOPS_HIDE_JOB_FEATURE_FLAG,
  );

export const useApplicantInsightNumberOfApplicantsFeatureFlag = () =>
  useExperimentValue<ApplicantInsightNumberOfApplicantsExperiment>(
    APPLICANT_INSIGHTS_NUMBER_OF_APPLICANTS_FEATURE_FLAG,
  );

export const useEnableNumberOfApplicants = () => {
  const flag = useApplicantInsightNumberOfApplicantsFeatureFlag();
  return flag === 'volume' || flag === 'count';
};

export const useLocationPromptFeatureFlag = () => {
  const value = useExperimentValue<LocationPromptExperiment>(
    LOCATION_PROMPT_FEATURE_FLAG,
  );

  return value === 'location-prompt';
};

export const useInFlowNudgingFeatureFlag = () => {
  const value = useExperimentValue<InFlowNudgingExperiment>(
    IN_FLOW_NUDGING_FEATURE_FLAG,
  );

  return {
    inFlowNudgingWithoutCounts: value === InFlowNudgingWithoutCounts,
    inFlowNudgingWithCounts: value === InFlowNudgingWithCounts,
  };
};

export const useEnableJdvJobMode = () =>
  Boolean(useFeatureFlag<JdvJobModeFeatureFlag>(JDV_JOB_MODE_FEATURE_FLAG));

export const useCareerFeedJobAiSummariesFeatureFlag = () =>
  useExperimentValue<CareerFeedJobAiSummariesExperiment>(
    CAREER_FEED_JOB_AI_SUMMARIES_FEATURE_FLAG,
  );

export const useCareerFeedJobAiSummariesEnabled = () => {
  const flag = useCareerFeedJobAiSummariesFeatureFlag();
  return flag === 'replace' || flag === 'all';
};

export const useIsStructuredFeedbackLoopsPhase1Enabled = () =>
  useFeatureFlag<StructuredFeedbackLoopsPhase1FeatureFlag>(
    STRUCTURED_FEEDBACK_LOOPS_PHASE_1_FEATURE_FLAG,
  );

export const useEnableJdvJobMatchAnalysis = () =>
  useExperimentValue<JobMatchAnalysisExperiment>(
    JDV_JOB_MATCH_ANALYSIS_FEATURE_FLAG,
  ) === 'show';
